// apps/hr/src/pages/hr/FreelancerEarnings.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, DollarSign, User, Calendar, CheckCircle, Clock, Filter, RefreshCw } from 'lucide-react'; // ✅ Added RefreshCw
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const formatPrice = (price) => {
  if (!price) return '₹0';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(price);
};

const FreelancerEarnings = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [designerFilter, setDesignerFilter] = useState('all');

  // Fetch earnings with designer details
  const { data: earnings = [], isLoading, refetch } = useQuery({
    queryKey: ['freelancerEarnings', statusFilter, designerFilter],
    queryFn: async () => {
      let filter = '';
      if (statusFilter !== 'all') filter += `status = "${statusFilter}"`;
      if (designerFilter !== 'all') {
        if (filter) filter += ' && ';
        filter += `designerId = "${designerFilter}"`;
      }
      return pb.collection('designer_earnings').getFullList({
        sort: '-weekStart',
        filter: filter || undefined,
        expand: 'designerId,taskId,projectId',
      });
    },
  });

  // Fetch designers list for filter
  const { data: designers = [] } = useQuery({
    queryKey: ['designers'],
    queryFn: () => pb.collection('users').getFullList({ filter: 'role = "freelancer" || role = "designer"', sort: 'name' }),
  });

  // Update payment status mutation
  const updateStatus = useMutation({
    mutationFn: ({ id, status }) => pb.collection('designer_earnings').update(id, { status, paidDate: status === 'paid' ? new Date().toISOString() : null }),
    onSuccess: () => {
      toast.success('Payment status updated');
      queryClient.invalidateQueries(['freelancerEarnings']);
      refetch();
    },
    onError: (err) => toast.error('Failed to update: ' + err.message),
  });

  const handleStatusChange = (id, newStatus) => {
    if (window.confirm(`Mark this earning as ${newStatus}?`)) {
      updateStatus.mutate({ id, status: newStatus });
    }
  };

  // Filter earnings
  const filteredEarnings = earnings.filter(e => {
    const designer = e.expand?.designerId;
    const task = e.expand?.taskId;
    const searchLower = search.toLowerCase();
    return (
      designer?.name?.toLowerCase().includes(searchLower) ||
      task?.title?.toLowerCase().includes(searchLower) ||
      e.amount?.toString().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-6 shadow-xl">
        <div className="relative">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <DollarSign className="w-6 h-6" /> Freelancer Earnings
          </h1>
          <p className="text-white/80">Manage and update designer payments</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by designer, task, amount..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
        <Select value={designerFilter} onValueChange={setDesignerFilter}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Designer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Designers</SelectItem>
            {designers.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => refetch()}><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
      </div>

      {/* Earnings Table */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
              <TableRow>
                <TableHead className="text-indigo-700 font-semibold">Designer</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Task</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Project</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Week</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Amount</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Status</TableHead>
                <TableHead className="text-indigo-700 font-semibold text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : filteredEarnings.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No earnings found</TableCell></TableRow>
              ) : (
                filteredEarnings.map((earning) => {
                  const designer = earning.expand?.designerId;
                  const task = earning.expand?.taskId;
                  const project = earning.expand?.projectId;
                  const statusColor = earning.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700';
                  return (
                    <TableRow key={earning.id} className="hover:bg-indigo-50/50 transition-colors">
                      <TableCell className="font-medium">{designer?.name || '—'}</TableCell>
                      <TableCell>{task?.title || '—'}</TableCell>
                      <TableCell>{project?.name || '—'}</TableCell>
                      <TableCell>{formatDate(earning.weekStart)} - {formatDate(earning.weekEnd)}</TableCell>
                      <TableCell className="font-bold text-amber-600">{formatPrice(earning.amount)}</TableCell>
                      <TableCell>
                        <Badge className={statusColor}>{earning.status === 'paid' ? '✅ Paid' : '⏳ Pending'}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex gap-2 justify-center">
                          {earning.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => handleStatusChange(earning.id, 'paid')}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Mark Paid
                            </Button>
                          )}
                          {earning.status === 'paid' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              onClick={() => handleStatusChange(earning.id, 'pending')}
                            >
                              <Clock className="w-4 h-4 mr-1" /> Revert
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default FreelancerEarnings;